import {
  Component,
  ViewChild,
  TemplateRef,
  OnInit,
  ElementRef
} from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';
import { MatSnackBar } from '@angular/material/snack-bar';
import { messageService } from 'src/app/message.service';
import { PaginationService } from 'src/app/PaginationService';
import { SuccessSnackbarComponent } from 'src/app/support/success-snackbar/success-snackbar.component';
import { AppService } from 'src/app/app.service';
import { DomSanitizer } from '@angular/platform-browser';
import { DatePipe } from '@angular/common';
import { ExcelPdfComponent } from 'src/app/support/excel-pdf/excel-pdf.component';
import { GridReorderComponent } from '../../grid-reorder/grid-reorder.component';
import { DynamicAuditTrailComponent } from 'src/app/support/dynamic-audit-trail/dynamic-audit-trail.component';

@Component({
  selector: 'app-org-printers',
  templateUrl: './org-printers.component.html',
  styleUrls: ['./org-printers.component.scss']
})
export class OrgPrintersComponent implements OnInit {

  isProcessing = true;
  primObj: any = {};
  popupTitle = '';
  visibleAddButton = false;
  visibleEditButton = false;
  searchValue: any;

  // ── Grid Data (Master Printers Configuration) ─────────
  printers: any[] = [];
  printersTemp: any[] = [];
  pagedItems: any[] = [];

  // Pagination Settings (Master Printers Grid)
  maxSize = 3;
  perPage = 10;
  pager: any = {};

  // Sorting State (Master Printers Grid)
  sortedColumn = '';
  sortDirection = 'asc';

  // Primary Execution Key Trackers
  editingPrinterId: number;
  deletingPrinterId: number;

  // Data Column Schema definitions for Printers Listing
  columns = [
    { key: 'printerName', label: 'Printer Name', width: '200px' },
    { key: 'displayPrinterName', label: 'Display Name', width: '200px' },
    { key: 'ipAddress', label: 'IP Address', width: '160px' },
    { key: 'siteId', label: 'Site Id', width: '160px' },
    { key: 'printerLocation', label: 'Location', width: '200px' },
    { key: 'isActive', label: 'Is Active', width: '120px' },
    { key: 'createdOn', label: 'Created On', width: '150px' },
  ];

  // ── Dynamic Meta-Engine Structural Properties (Print Logs) ──
  reportHeaderTemplateList: any[] = [];
  reportHeaderTemplateTempList: any[] = [];
  logPagedItems: any[] = [];
  logPager: any = {};
  logPerPage = 10;
  logCollectionSize = 0;
  logSortedColumn = '';
  logSortDirection = 'asc';
  logSearchValue: any;
  logColumnLists: any = {};
  logMakeStickyColumns: any;
  logDataTypeList: any[] = [];
  logDynamicLabel: any = null;
  logLabel: any[] = [];
  logReportObj: any = {};
  logAuditTrail: any = {};
  visibleLogAuditTrail = false;
  previousLogSelectedIdx: any;
  previewData: any;
  isLogsProcessing = false;

  // ── Dialog Windows Modal View References ───────────────
  @ViewChild('addAndEditPopup', { static: true }) addAndEditPopup!: TemplateRef<any>;
  visibleAddAndEditPopup!: MatDialogRef<TemplateRef<any>>;

  @ViewChild('deletePopup', { static: true }) deletePopup!: TemplateRef<any>;
  visibleDeletePopup!: MatDialogRef<TemplateRef<any>>;

  @ViewChild('printLogsPopup', { static: true }) printLogsPopup!: TemplateRef<any>;
  visiblePrintLogsPopup!: MatDialogRef<TemplateRef<any>>;

  @ViewChild('previewPopup', { static: true }) previewPopup!: TemplateRef<any>;
  visiblePreviewPopup!: MatDialogRef<TemplateRef<any>>;

  @ViewChild('excelPdfPopupLog') excelPdfPopupLog!: ExcelPdfComponent;
  @ViewChild('gridReorderPopupLog') gridReorderPopupLog!: GridReorderComponent;
  @ViewChild('auditTrailPopupLog') auditTrailPopupLog!: DynamicAuditTrailComponent;
  excelPdfLog: any = {};

  // ── Structural Popups Layout Bounds Configurations ─────
  addAndEditPopupConfig = {
    width: '70%',
    height: 'auto',
    panelClass: 'my-dialog',
    hasBackdrop: true,
    disableClose: true,
    position: { top: '60px' },
  };

  deletePopupConfig = {
    width: '500px',
    height: 'auto',
    panelClass: 'my-dialog',
    hasBackdrop: false,
    disableClose: true,
    position: { top: '10px' },
  };

  printLogsPopupConfig = {
    width: '85%',
    height: '80%',
    panelClass: 'my-dialog',
    hasBackdrop: true,
    disableClose: true,
    position: { top: '30px' },
  };

  previewPopupConfig = {
    width: '1000px',
    height: 'auto',
    panelClass: 'my-dialog',
    hasBackdrop: true,
    disableClose: true,
    position: { top: '100px' },
  };

  constructor(
    private readonly http: HttpClient,
    private readonly dialog: MatDialog,
    private readonly snackBar: MatSnackBar,
    private readonly appService: AppService,
    private readonly paginationService: PaginationService,
    public readonly message: messageService,
    private readonly elem: ElementRef,
    public sanitizer: DomSanitizer,
    private readonly datePipe: DatePipe
  ) { }

  ngOnInit(): void {
    this.message.breadcrumbData.secondLevel = 'Printers';
    this.getCall();
  }

  // ── Primary Grid Management Controllers (Printers View) ──
  getCall(): void {
    this.isProcessing = true;
    this.appService.getOrgPrinters().subscribe({
      next: (data: any) => {
        this.printers = Array.isArray(data) ? data : [];
        this.printersTemp = [...this.printers];
        this.setPage(1, 0);
        this.isProcessing = false;
      },
      error: () => {
        this.isProcessing = false;
        this.showSnackBar('Failed to load printers', 'E');
      }
    });
  }

  refreshGrid(): void {
    this.printers = [];
    this.printersTemp = [];
    this.getCall();
  }

  globalFilter(event: Event): void {
    const filter = (event.target as HTMLInputElement).value.trim().toLowerCase();
    if (!filter) {
      this.printers = [...this.printersTemp];
    } else {
      this.printers = this.printersTemp.filter(
        (item: any) => JSON.stringify(item).toLowerCase().includes(filter)
      );
    }
    this.setPage(1, 0);
  }

  columnFilter(column: string, event: Event): void {
    const filter = (event.target as HTMLInputElement).value.trim().toLowerCase();
    if (!filter) {
      this.pagedItems = this.printers.slice(this.pager.startIndex, this.pager.endIndex + 1);
    } else {
      this.pagedItems = this.printers.filter(
        (item: any) => item[column]?.toString().toLowerCase().includes(filter)
      );
    }
  }

  sortColumn(column: string): void {
    if (column === this.sortedColumn) {
      this.sortDirection = this.sortDirection === 'asc' ? 'desc' : 'asc';
    } else {
      this.sortedColumn = column;
      this.sortDirection = 'asc';
    }
    this.printers.sort((a, b) => {
      const va = a[column];
      const vb = b[column];
      if (va < vb) return this.sortDirection === 'asc' ? -1 : 1;
      if (va > vb) return this.sortDirection === 'asc' ? 1 : -1;
      return 0;
    });
    this.setPage(1, 0);
  }

  setPage(page: number, targetPage: number): void {
    if (page < 1) return;
    this.pager = this.paginationService.getPager(this.printers.length, page, this.perPage, targetPage);
    this.pagedItems = this.printers.slice(this.pager.startIndex, this.pager.endIndex + 1);
  }

  paginationCall(value: number): void {
    this.perPage = value;
    this.setPage(this.pager.currentPage, 0);
  }

  // ── Core Printer Mutators (Full Access Logic) ─────────
  openAddPopup(): void {
    this.visibleAddButton = true;
    this.visibleEditButton = false;
    this.popupTitle = 'Add Printer';
    this.primObj = { isActiveBool: true, printerName: '', displayPrinterName: '', ipAddress: '', siteId: '', printerLocation: '' };
    this.visibleAddAndEditPopup = this.dialog.open(this.addAndEditPopup, this.addAndEditPopupConfig);
  }

  openEditPopup(printer: any): void {
    this.isProcessing = true;
    this.visibleAddButton = false;
    this.visibleEditButton = true;
    this.popupTitle = 'Edit Printer - ' + printer.printerName;
    this.editingPrinterId = printer.printerId;
    this.visibleAddAndEditPopup = this.dialog.open(this.addAndEditPopup, this.addAndEditPopupConfig);
    this.appService.getOrgPrinterById(printer.printerNo).subscribe({
      next: (data: any) => {
        this.primObj = { ...data, isActiveBool: data.isActive === 'Y', updateReason: '' };
        this.isProcessing = false;
      },
      error: () => {
        this.isProcessing = false;
        this.showSnackBar('Failed to load printer data context', 'E');
      }
    });
  }

  openDeletePopup(printer: any): void {
    this.popupTitle = 'Delete Printer';
    this.deletingPrinterId = printer.printerNo;
    this.primObj = { ...printer, deleteReason: '' };
    this.visibleDeletePopup = this.dialog.open(this.deletePopup, this.deletePopupConfig);
  }

  saveFunction(): void {
    const validations = [
      { condition: !this.primObj.printerName, id: 'printer-name' },
      { condition: !this.primObj.ipAddress, id: 'ip-address' },
      { condition: !this.primObj.updateReason && this.visibleEditButton, id: 'update-reason' },
      { condition: !this.primObj.siteId, id: 'site-id' },
      { condition: !this.primObj.printerLocation, id: 'printer-location' }
    ];

    const invalidIds = validations.filter(v => v.condition).map(v => v.id);
    if (invalidIds.length > 0) {
      this.fieldValidation(invalidIds);
      this.showSnackBar('Please enter all Mandatory Fields!', 'E');
      return;
    }

    this.isProcessing = true;
    const payload = {
      printerNo: this.primObj.printerNo ?? null,
      printerName: this.primObj.printerName,
      displayPrinterName: this.primObj.displayPrinterName,
      ipAddress: this.primObj.ipAddress,
      siteId: this.primObj.siteId,
      printerLocation: this.primObj.printerLocation,
      isActive: this.primObj.isActiveBool ? 'Y' : 'N',
      deleteReason: null,
      operation: this.visibleAddButton ? 'I' : 'U'
    };

    const apiCall = this.visibleAddButton
      ? this.appService.addOrgPrinter(payload)
      : this.appService.updateOrgPrinter(payload);

    apiCall.subscribe({
      next: (res: any) => {
        this.isProcessing = false;
        if (res?.resultType === 'S' || res?.result === 'S') {
          this.showSnackBar(res.result || 'Operation successful', 'S');
          this.closeAddEditPopup();
          this.getCall();
        } else {
          this.showSnackBar(res?.result || 'Operation execution failure', 'E');
        }
      },
      error: () => {
        this.isProcessing = false;
        this.showSnackBar('Failed to synchronize printer master node', 'E');
      }
    });
  }

  deleteFunction(): void {
    if (!this.primObj.deleteReason) {
      const field = document.getElementById('delete-reason') as HTMLInputElement;
      if (field) field.style.color = 'red';
      this.showSnackBar('Please enter Delete Reason', 'E');
      return;
    }

    this.isProcessing = true;
    const payload = {
      ...this.primObj,
      printerNo: this.deletingPrinterId,
      isActive: 'N',
      operation: 'D'
    };

    this.appService.deleteOrgPrinter(payload).subscribe({
      next: (res: any) => {
        this.isProcessing = false;
        if (res?.resultType === 'S' || res?.result === 'S') {
          this.showSnackBar(res.result || 'Printer purged successfully', 'S');
          this.closeDeletePopup();
          this.getCall();
        } else {
          this.showSnackBar(res?.result || 'Purge tracking execution rejected', 'E');
        }
      },
      error: () => {
        this.isProcessing = false;
        this.showSnackBar('Failed to update drop sequence flag status', 'E');
      }
    });
  }

  // ── Audit Logs Meta-Driven Engine Logic (Read-Only) ────
  openPrintLogsPopup(): void {
    this.isLogsProcessing = true;
    this.reportHeaderTemplateList = [];
    this.reportHeaderTemplateTempList = [];
    this.logPagedItems = [];
    this.logSearchValue = '';

    const metaPayload = {

      gridName: "ORG_PRINT_LOG"
    };

    this.visiblePrintLogsPopup = this.dialog.open(this.printLogsPopup, this.printLogsPopupConfig);

    this.appService.getDynamicGridHeaders(metaPayload).subscribe({
      next: (response: any) => {
        response.orderByClause ??= "";
        this.logMakeStickyColumns = response.fixedFieldCnt;

        const dataQueryPayload: any = {

          gridName: "DMS_ORG_PRINT_LOG_VW",
          tableOrViewName: response.tableOrViewName ?? "ONEQMS_ORG_PRINT_LOG",
          columnsList: response.columnsList ?? "",
          fieldLabelsList: response.fieldLabelsList ?? "",
          invisibleColumn: response.invisibleColumn ?? "",
          orderByClause: response.orderByClause ?? "",
          fieldSizeList: response.fieldSizeList ?? "",
          columnsListarray: [],
          fieldColumnarray: [],
          invisibleColumnarray: [],
          fieldSizeListArray: [],
          manualEntryDataFields: response.manualEntryDataFields,
          clobColumnlist: response.closblist || "",
          blobColumnlist: response.bloblist || ""
        };
        this.logReportObj = { ...dataQueryPayload, ...metaPayload };

        this.appService.getDynamicGridData(dataQueryPayload).subscribe({
          next: (responseRecords: any) => {
            this.reportHeaderTemplateList = responseRecords;
            this.reportHeaderTemplateTempList = [...this.reportHeaderTemplateList];
            this.visibleLogAuditTrail = false;

            dataQueryPayload.columnsListarray = dataQueryPayload.columnsList.split(",");
            dataQueryPayload.fieldColumnarray = dataQueryPayload.fieldLabelsList.split(",");
            dataQueryPayload.invisibleColumnarray = dataQueryPayload.invisibleColumn.split(",");
            dataQueryPayload.fieldSizeListArray = dataQueryPayload.fieldSizeList.split(",");

            this.logColumnLists.columnsListarray = dataQueryPayload.columnsListarray;
            this.logColumnLists.fieldLabelsList = dataQueryPayload.fieldLabelsList;
            this.logColumnLists.fieldColumnarray = [];

            for (let p = 0; p < dataQueryPayload.fieldColumnarray.length; p++) {
              this.logColumnLists.fieldColumnarray.push({
                "fieldColumnarray": dataQueryPayload.fieldColumnarray[p],
                "width": dataQueryPayload.fieldSizeListArray[p] + 'px'
              });
            }
            this.logColumnLists.invisibleColumnarray = dataQueryPayload.invisibleColumnarray;
            this.logColumnLists.fieldSizeListArray = [];
            for (const size of dataQueryPayload.fieldSizeListArray) {
              this.logColumnLists.fieldSizeListArray.push(size + 'px');
            }

            let y = 0;
            while (y < this.logColumnLists.columnsListarray.length) {
              const currentColumn = this.logColumnLists.columnsListarray[y];
              const isInvisible = this.logColumnLists.invisibleColumnarray.includes(currentColumn);
              if (isInvisible) {
                this.logColumnLists.columnsListarray.splice(y, 1);
                this.logColumnLists.fieldColumnarray.splice(y, 1);
                this.logColumnLists.fieldSizeListArray.splice(y, 1);
              } else {
                y++;
              }
            }

            let tableData = { tablename: dataQueryPayload.tableOrViewName };
            this.logColumnLists.columnsListarray1 = [];

            this.appService.tableFieldsDataType(tableData).subscribe({
              next: (responseTypes: any) => {
                this.logDataTypeList = responseTypes;
                for (let m = 0; m < this.logColumnLists.columnsListarray.length; m++) {
                  this.logColumnLists.columnsListarray1.push({});
                  this.logColumnLists.columnsListarray1[m].columnsListarray = this.logColumnLists.columnsListarray[m];
                  this.logColumnLists.columnsListarray1[m].dataType = "textbox";
                  this.logColumnLists.columnsListarray1[m].width = this.logColumnLists.fieldSizeListArray;
                  for (const dataType of this.logDataTypeList) {
                    if (dataType.columnName == this.logColumnLists.columnsListarray[m]) {
                      this.logColumnLists.columnsListarray1[m].dataType = dataType.fieldType;
                    }
                  }
                }
                this.logCollectionSize = this.reportHeaderTemplateList.length;
                this.setLogPage(1, 0);
                this.logDynamicLabelCall();
                this.isLogsProcessing = false;
              },
              error: () => { this.isLogsProcessing = false; }
            });
          },
          error: () => { this.isLogsProcessing = false; }
        });
      },
      error: () => { this.isLogsProcessing = false; }
    });
  }

  getPrintLogsPopup(): void {
    this.openPrintLogsPopup();
  }

  logDynamicLabelCall() {
    const data = {
      tableName: this.logReportObj.tableOrViewName,
      moduleName: null,
      gridName: this.logReportObj.gridName,
      groupId: 0,
    };
    this.appService.getreorderGirdColumnList(data).subscribe({
      next: (dataLabels: any) => {
        this.logLabel = dataLabels;
        this.logDynamicLabel = this.logLabel.reduce((acc: any, user: any) => {
          acc[`${user.columnName}`] = user.changedFieldName;
          return acc;
        }, {});
      }
    });
  }

  setLogPage(page: number, targetPage: number) {
    if (page < 1 || (this.logPager.totalPages && page > this.logPager.totalPages)) return;
    if (Number.isNaN(page)) page = 1;
    this.logPager = this.paginationService.getPager(this.reportHeaderTemplateList.length, page, this.logPerPage, targetPage);
    this.logPagedItems = this.reportHeaderTemplateList.slice(this.logPager.startIndex, this.logPager.endIndex + 1);
  }

  logPaginationCall(value: number) {
    this.logPerPage = value;
    this.setLogPage(this.logPager.currentPage, 0);
  }

  setLogPagedOrSearch(page: number, targetPage: number) {
    if (Number.isNaN(page)) page = 1;
    if (this.reportHeaderTemplateList.length > 0) {
      this.logPager = this.paginationService.getPager(this.reportHeaderTemplateList.length, page, this.logPerPage, targetPage);
      this.logPagedItems = this.reportHeaderTemplateList.slice(this.logPager.startIndex, this.logPager.endIndex + 1);
    } else {
      this.logPagedItems = [];
    }
  }

  logGlobalFilter(event: Event) {
    const filter = (event.target as HTMLInputElement).value.trim().toLowerCase();
    if (filter === '') {
      this.reportHeaderTemplateList = [...this.reportHeaderTemplateTempList];
      this.logPager = this.paginationService.getPager(this.reportHeaderTemplateList.length, 1, this.logPerPage, 0);
      this.logPagedItems = this.reportHeaderTemplateList.slice(this.logPager.startIndex, this.logPager.endIndex + 1);
    } else {
      if (!this.reportHeaderTemplateTempList || !this.reportHeaderTemplateTempList.length) return;
      this.reportHeaderTemplateList = this.reportHeaderTemplateTempList.filter((item: any) => {
        return JSON.stringify(item).toLowerCase().includes(filter);
      });
      this.setLogPagedOrSearch(1, 0);
    }
  }

  logColumnSearch(column: string, event: Event, dataType: any): void {
    const filter = (event.target as HTMLInputElement).value.trim().toLowerCase();
    if (filter === '') {
      this.logPagedItems = this.reportHeaderTemplateList.slice(this.logPager.startIndex, this.logPager.endIndex + 1);
    } else {
      this.logPagedItems = this.reportHeaderTemplateList.filter((item: any) => {
        const columnValue = item[column];
        if (dataType === 'd') {
          if (columnValue == null) return false;
          const formattedDate = this.datePipe.transform(columnValue, 'dd-MMM-yyyy');
          return formattedDate?.toString().toLowerCase().includes(filter) ?? false;
        } else if (typeof columnValue === 'string') {
          return columnValue.toLowerCase().includes(filter);
        } else if (typeof columnValue === 'number') {
          return columnValue.toString().toLowerCase().includes(filter);
        }
        return false;
      });
    }
  }

  logSortColumn(column: string, list: any) {
    if (column === this.logSortedColumn) {
      this.logSortDirection = this.logSortDirection === 'asc' ? 'desc' : 'asc';
    } else {
      this.logSortedColumn = column;
      this.logSortDirection = 'asc';
    }
    list.showsort = this.logSortDirection;

    this.reportHeaderTemplateList.sort((a: any, b: any) => {
      const valueA = a[column];
      const valueB = b[column];
      if (valueA < valueB) return this.logSortDirection === 'asc' ? -1 : 1;
      if (valueA > valueB) return this.logSortDirection === 'asc' ? 1 : -1;
      return 0;
    });
    this.setLogPage(1, 0);
  }

  logChildCheckBoxChecked(e: any, list: any, idx: any) {
    if (e.target.checked) {
      if (this.previousLogSelectedIdx != undefined && this.reportHeaderTemplateList[this.previousLogSelectedIdx]) {
        this.reportHeaderTemplateList[this.previousLogSelectedIdx].childCheckBoxs = false;
      }
      list.childCheckBoxs = true;
      this.logAuditTrail.refNo = list.PRINT_LOG_ID;
    } else {
      list.childCheckBoxs = false;
    }
    this.previousLogSelectedIdx = idx;
    this.visibleLogAuditTrail = this.reportHeaderTemplateList.some((l: any) => l.childCheckBoxs);
  }

  getClobData(list: any, columnName: any, event: any) {
    event.stopPropagation();
    this.isLogsProcessing = true;
    let docObj = {

      primeIdentifier: list.PRINT_LOG_ID,
      tableView: this.logReportObj.tableOrViewName,
      columnName: columnName
    };
    this.appService.getClobToViewAudit(docObj).subscribe({
      next: (Response: any) => {
        this.previewTemplate(Response.result);
        this.isLogsProcessing = false;
      },
      error: () => { this.isLogsProcessing = false; }
    });
  }

  previewTemplate(value: any) {
    this.previewData = this.sanitizer.bypassSecurityTrustHtml(value);
    this.visiblePreviewPopup = this.dialog.open(this.previewPopup, this.previewPopupConfig);
  }

  cancelPreviewFunction() { this.visiblePreviewPopup.close(); }

  openLogReorder() {
    this.gridReorderPopupLog.reordercall(this.logReportObj.gridName, this.logReportObj.tableOrViewName, null, 0);
  }

  openLogAuditTrail(viewType: any, notification: any) {
    this.logAuditTrail.refNo = viewType == 'overAll' ? null : notification.PRINT_LOG_ID;
    this.logAuditTrail.gridName = 'ORG_PRINT_LOG';
    this.logAuditTrail.report_And_Popup_Name = 'Organization Print Log Audit Trail';
    this.auditTrailPopupLog.triggerTrail(notification);
  }

  // ── Document Export Naming Implementation ─────────────
  openLogReport(reportType: string) {
    const currentFormattedDateTime = this.datePipe.transform(new Date(), 'dd-MMM-yyyy_HH-mm-ss');
    this.excelPdfLog.reportObj = this.logReportObj;
    this.excelPdfLog.reportType = 'Dynamic';
    // Force strict format configuration criteria parameters mapping
    this.excelPdfLog.reportName = `Organization_Print_Logs_${currentFormattedDateTime}`;
    this.excelPdfLog.dropDown = this.logColumnLists?.fieldColumnarray?.map((item: any) => item.fieldColumnarray);
    this.excelPdfPopupLog.openReportPopup(reportType);
  }

  // ── Interface Grid Layout Sticky CNG Alignments ────────
  logCssFunction(ix: number) {
    let elements1 = this.elem.nativeElement.querySelectorAll('.sticky-dynamic-col1-log');
    let index1 = 1; let headerWidth = 0;
    elements1.forEach((header: any) => {
      for (let i = 1; i <= this.logMakeStickyColumns; i++) {
        if (index1 == i) {
          header.style.position = 'sticky'; header.style.background = 'white';
          if (index1 > 1) { headerWidth += 200; header.style.left = headerWidth + 'px'; }
          else { headerWidth = 60; header.style.left = headerWidth + 'px'; }
        }
      }
      index1++;
    });

    let elements2 = this.elem.nativeElement.querySelectorAll('.sticky-dynamic-row1-log');
    let index2 = 1; let lengthOfColumns = this.logColumnLists.columnsListarray1.length; let colWidth = 0;
    elements2.forEach((column: any) => {
      for (let j = 1; j <= this.logMakeStickyColumns; j++) {
        if (index2 == j) {
          column.style.position = 'sticky'; column.style.background = 'white';
          if (index2 > 1) { colWidth += 200; column.style.left = colWidth + 'px'; }
          else { colWidth = 60; column.style.left = colWidth + 'px'; }
        }
      }
      if (index2 == (lengthOfColumns - 1)) index2 = 0; else index2++;
    });
  }

  // ── Structural Utility Handlers ───────────────────────
  fieldValidation(elementIds: string[]): void {
    elementIds.forEach((id) => {
      const el = document.getElementById(id) as HTMLInputElement;
      if (el) el.style.color = 'red';
    });
  }

  onNgModelChange(value: any, fieldId: string): void {
    const el = document.getElementById(fieldId) as HTMLInputElement;
    if (el) el.style.color = value ? '#005c8a' : 'red';
  }

  closeAddEditPopup(): void { this.visibleAddAndEditPopup.close(); }
  closeDeletePopup(): void { this.visibleDeletePopup.close(); }
  closePrintLogsPopup(): void { this.visiblePrintLogsPopup.close(); }
  onChildValueEmitted() { this.getCall(); }
  onLogChildValueEmitted() { this.setLogPage(1, 0); }

  showSnackBar(message: string, type: string): void {
    const sb = this.snackBar.openFromComponent(SuccessSnackbarComponent, {
      duration: type === 'S' ? 5000 : undefined,
      data: { message, type: type === 'S' ? 'Success' : 'Error', buttonLabel: 'Ok', pageName: 'Printers' },
      horizontalPosition: 'center', verticalPosition: 'top', panelClass: type === 'S' ? 'Success' : 'Error',
    });
    sb.onAction().subscribe(() => sb.dismiss());
  }

  cssfunction(ix: number) { }
}
